import type { JoinMethod } from '@pages/JoinTrip/core'
import { getDeviceContext } from '@lib/analytics'
import api from '@lib/api'
import { isJoinRequestPending } from '@lib/apiError'
import { queryKeys } from '@lib/queryKeys'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Trip } from '@types'

export interface JoinTripInput {
	code: string
	/** How the person got here — a Join link, or a code typed by hand. */
	method: JoinMethod
}

/**
 * Joins the signed-in user to a trip by code. The request carries this
 * browser's device context so the *server* writes the `trip_joined` event,
 * the same way sign-in is recorded — the webapp never names that event itself.
 */
export const useJoinTrip = () => {
	const queryClient = useQueryClient()

	return useMutation<Trip, Error, JoinTripInput>({
		mutationFn: async ({ code, method }) => {
			const response = await api.post<Trip>(`/trips/${code}/join`, {
				analytics: getDeviceContext(),
				method,
			})
			return response.data
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.trips() })
		},
		// On a trip with Join approval on, the join became a Join request: it shows in my trips list.
		onError: error => {
			if (isJoinRequestPending(error)) queryClient.invalidateQueries({ queryKey: queryKeys.myJoinRequests() })
		},
	})
}
